import LinkCusstom from '@/components/common/LinkCusstom'
import SvgIcon from '@/components/common/SvgIcon'
import { Tooltip } from 'antd'
import {
  AlarmClockCheck,
  AudioLines,
  BadgeDollarSign,
  Banknote,
  BarChart3,
  BookOpenText,
  BookUser,
  BriefcaseBusiness,
  Calendar,
  CalendarCheck,
  CalendarCheck2,
  CalendarCheck2Icon,
  CalendarRange,
  ChartColumnIncreasing,
  CircleUser,
  ClipboardCheck,
  ClipboardMinus,
  Clock3,
  ClockAlert,
  Command,
  Contact,
  DoorOpen,
  FileCheck2,
  FileQuestion,
  FileText,
  FileUser,
  FolderSync,
  Gift,
  HeartHandshake,
  Home,
  Info,
  LandPlot,
  Layers,
  LayoutDashboard,
  ListChecks,
  MessageCircle,
  MessageSquareMore,
  Network,
  Receipt,
  RefreshCcw,
  ScanEye,
  ScrollText,
  Settings,
  SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  SquareUserRound,
  Star,
  Tag,
  Tickets,
  TicketsIcon,
  UserCheck,
  UserCog,
  UserCog2,
  UserPlus,
  UserSearch,
  Users,
} from 'lucide-react'
import { ADMIN_MENU_CONFIG, USER_MENU_CONFIG } from './MenuConfig'
import ROUTER from './ROUTER'

// Type definition matches original
export interface MenuItem {
  key: string
  label: any
  tabid?: string[]
  children?: MenuItem[]
  [key: string]: any
}

// Icon Mapper
const IconMap: Record<string, any> = {
  AudioLines,
  BookOpenText,
  BookUser,
  CalendarCheck,
  Command,
  FileText,
  FolderSync,
  Gift,
  Info,
  ListChecks,
  MessageCircle,
  MessageSquareMore,
  Network,
  ScrollText,
  Settings,
  ShieldAlert,
  SquareUserRound,
  Home,
  RefreshCcw,
  BarChart3,
  CalendarRange,
  Layers,
  DoorOpen,
  ScanEye,
  Users,
  UserCheck,
  SettingsIcon,
  ShieldCheck,
  Calendar,
  CalendarCheck2,
  ClipboardMinus,
  Tickets,
  LandPlot,
  TicketsIcon,
  Tag,
  ClockAlert,
  CalendarCheck2Icon,
  UserCog,
  Contact,
  Banknote,
  ClipboardCheck,
  UserPlus,
  FileQuestion,
  Star,
  HeartHandshake,
  LayoutDashboard,
  CircleUser,
  UserSearch,
  UserCog2,
  Clock3,
  BadgeDollarSign,
  AlarmClockCheck,
  BriefcaseBusiness,
  FileCheck2,
  ChartColumnIncreasing,
  Receipt,
  FileUser,
}

const getIcon = (name: string) => {
  const Icon = IconMap[name]
  return Icon ? <Icon size={16} /> : null
}

// Recursive Mapper
const mapConfigToMenuItem = (item: any): MenuItem => {
  const hasPath = !!item.path

  return {
    ...item,
    key: item.key,
    label: hasPath ? (
      <LinkCusstom to={item.path}>{item.label}</LinkCusstom>
    ) : item?.type === 'group' ? (
      <div className="max-line1 fs-8 fw-600 pl-5" style={{ color: 'var(--color-primary)' }}>
        <Tooltip title={item.label} placement="right">
          {item.label}
        </Tooltip>
      </div>
    ) : (
      <div className="max-line1">
        <Tooltip title={item.label} placement="right">
          {item.label}
        </Tooltip>
      </div>
    ),
    icon: item.icon ? getIcon(item.icon) : null,
    tabid: item.tabid,
    children: item.children?.map(mapConfigToMenuItem),
  }
}

export const MenuItemAdmin = (): MenuItem[] => {
  return ADMIN_MENU_CONFIG.map(mapConfigToMenuItem)
}

export const MenuItemUser = (): MenuItem[] => {
  return USER_MENU_CONFIG.map(mapConfigToMenuItem)
}

// Legacy exports kept for compatibility if needed
export const ListTitleRouter = () => []
export const MenuItemBreadcrumbs = () => [
  {
    key: 'group-Dashboards',
    label: 'Tổng quan',
    children: [{ key: ROUTER.THONG_KE_TONG_QUAN, label: 'Bảng thông tin' }],
  },
  {
    key: 'group-Categories',
    label: 'Danh mục dùng chung',
    children: [
      { key: ROUTER.LINH_VUC_KINH_DOANH, label: 'Lĩnh vực kinh doanh' },
      { key: ROUTER.LINH_VUC_HOAT_DONG, label: 'Lĩnh vực hoạt động' },
      { key: ROUTER.TINH_THANH, label: 'Tỉnh/Thành phố' },
      { key: ROUTER.QUAN_HUYEN, label: 'Quận/Huyện' },
      { key: ROUTER.XA_PHUONG, label: 'Xã/Phường' },
      { key: ROUTER.DON_VI_DO_LUONG, label: 'Đơn vị đo lường' },
      { key: ROUTER.DON_VI_QUAN_LY, label: 'Đơn vị quản lý' },
    ],
  },
  {
    key: 'group-Goods',
    label: 'Sản phẩm',
    children: [
      { key: ROUTER.DANH_MUC_SAN_PHAM, label: 'Loại sản phẩm' },
      { key: ROUTER.PRODUCT, label: 'Sản phẩm' },
      { key: ROUTER.PRODUCT_OCOP, label: 'Sản phẩm OCOP' },
      { key: ROUTER.DANG_KY_KHUYEN_MAI, label: 'Đăng ký khuyến mại' },
      { key: ROUTER.DANG_KY_SAN_PHAM, label: 'Đăng ký sản phẩm' },
      { key: ROUTER.PRODUCT_OCOP_REGISTER, label: 'Đăng ký sản phẩm OCOP' },
      { key: ROUTER.PRODUCT_PROMOTION, label: 'Khuyến mãi' },
      { key: ROUTER.PRODUCT_EVALUATION_DOSSIER, label: 'Quản lý hồ sơ đánh giá sản phẩm' },
    ],
  },
  {
    key: 'group-Ecommerces',
    label: 'Thương mại điện tử',
    children: [
      { key: ROUTER.DON_HANG, label: 'Quản lý đơn hàng' },
      { key: ROUTER.CUNG_CAU, label: 'Quản lý cung cầu' },
    ],
  },
  {
    key: 'group-Enterprises',
    label: 'Doanh nghiệp',
    children: [
      { key: ROUTER.DOANH_NGHIEP, label: 'Doanh nghiệp truy xuất nguồn gốc' },
      { key: ROUTER.DANG_KY_DOANH_NGHIEP, label: 'Đăng ký doanh nghiệp' },
    ],
  },
  {
    key: 'group-Partners',
    label: 'Đối tác',
    children: [
      { key: ROUTER.DOI_TAC_SAN_XUAT, label: 'Đối tác sản xuất ban đầu' },
      { key: ROUTER.DOI_TAC_SO_CHE, label: 'Đối tác sơ chế - chế biến' },
      { key: ROUTER.DOI_TAC_KHO_VAN, label: 'Đối tác kho vận' },
      { key: ROUTER.DOI_TAC_PHAN_PHOI, label: 'Đối tác phân phối' },
    ],
  },
  {
    key: 'group-Productions',
    label: 'Sản xuất',
    children: [
      { key: ROUTER.AN_TOAN_SAN_XUAT, label: 'Vệ sinh an toàn sản xuất' },
      { key: ROUTER.NHAT_KY_SAN_PHAM, label: 'Nhật ký sản phẩm' },
      { key: ROUTER.CHUOI_CUNG_UNG, label: 'Chuỗi cung ứng sản phẩm' },

      { key: ROUTER.DON_SAN_XUAT, label: 'Lô sản phẩm' },
      { key: ROUTER.VUNG_SAN_XUAT, label: 'Vùng sản xuất' },
    ],
  },
  {
    key: 'group-Reports',
    label: 'Báo cáo - Thống kê',
    children: [
      { key: ROUTER.BAO_CAO_DOANH_NGHIEP, label: 'Báo cáo doanh nghiệp' },
      { key: ROUTER.BAO_CAO_SAN_PHAM, label: 'Báo cáo sản phẩm' },
      { key: ROUTER.THONG_KE_TEM_NHAN, label: 'Thống kê tem nhãn' },
      { key: ROUTER.THONG_KE_SAN_PHAM, label: 'Thống kê sản phẩm' },
      { key: ROUTER.THONG_KE_DON_SAN_XUAT, label: 'Thống kê đơn sản xuất' },
      { key: ROUTER.THONG_KE_TEM_NHAN_DOANH_NGHIEP, label: 'Thống kê tem nhãn (DN)' },
      { key: ROUTER.THONG_KE_DON_HANG, label: 'Thống kê đơn hàng' },
      { key: ROUTER.THONG_KE_CUNG_CAU, label: 'Thống kê cung cầu' },
      { key: ROUTER.BAO_CAO_DOI_TAC, label: 'Báo cáo đối tác' },
      { key: ROUTER.THONG_KE_DOANH_NGHIEP_DN, label: 'Thống kê doanh nghiệp' },
    ],
  },
  {
    key: 'group-Settings',
    label: 'Cài đặt hệ thống',
    children: [
      { key: ROUTER.CAI_DAT_CHUNG_TIEN_TO, label: 'Cài đặt chung' },
      { key: ROUTER.CAI_DAT_LICH_LAM_VIEC, label: 'Cài đặt lịch làm việc' },
      { key: ROUTER.QUAN_LY_NGUOI_DUNG, label: 'Quản lý người dùng' },
      { key: ROUTER.NHAT_KY_HE_THONG, label: 'Nhật ký hệ thống' },
      { key: ROUTER.QUAN_LY_MENU, label: 'Quản lý menu' },
      { key: ROUTER.QUAN_LY_PHAN_QUYEN, label: 'Nhóm quyền' },
      // { key: ROUTER.QUAN_LY_DANH_MUC_CHUC_NANG, label: 'Danh mục chức năng' },
      { key: ROUTER.THIET_LAP_KET_NOI, label: 'Thiết lập kết nối' },
    ],
  },
  {
    key: 'group-Traces',
    label: 'Truy xuất - Tem nhãn',
    children: [
      { key: ROUTER.QUAN_LY_MA_TEM, label: 'Quản lý mã/tem' },
      { key: ROUTER.DANG_KY_MA_TEM, label: 'Đăng ký mã/tem' },
    ],
  },
  {
    key: 'group-User',
    label: 'Tài khoản',
    children: [
      { key: ROUTER.THONG_TIN_TAI_KHOAN, label: 'Thông tin cá nhân' },
      {
        key: (ROUTER as any)?.CHANGE_PASSWORD || '/user/change-password',
        label: 'Đổi mật khẩu',
      },
    ],
  },
]

const MenuItem = () => {
  return [
    {
      key: ROUTER.HOME,
      icon: <SvgIcon name="home" />,
      align: 'center',
    },
  ]
}
export default MenuItem
